import React, { useEffect, useState } from "react";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { useTranslation } from "react-i18next";
import NoDataComponent from "components/Common/NoData";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import { Form } from "antd";
import { useParams, useNavigate } from "react-router";
import TeacherStudentExamTypeMatrixTable from "../components/TeacherStudentExamTypeMatrixTable";
import useTeacher from "components/Peoples/Teacher/hooks/useTeacher";
import { useSelector } from "react-redux";



const TeacherMarkStudents: React.FC = () => {
    const { subjectId = "", classId = "", semester = "" } = useParams<{
        classId: string;
        subjectId: string;
        semester: string
    }>();

    const { t } = useTranslation();
    const [subjects, setSubjects] = useState<any[]>([]);
    const [selectedClassId, setSelectedClassId] = useState<string>(classId);
    const [selectedSubjectId, setSelectedSubjectId] = useState<string>(subjectId);
    const [selectedSemester, setSelectedSemester] = useState<string>(semester);
    const [formattedClasses, setFormattedClasses] = useState<Array<{ id: string; name: string }>>([]);

    const { classes, fetchClassesTeacher } = useTeacher();
    const teacherId = useSelector((state: any) => state.authSlice.user?.roleId);

    const breadcrumbItems = [
        { label: "dashboard", path: all_routes.dashboard },
        { label: "mark_matrix.title", active: true },
    ];

    const hasAllParams = classId && subjectId && semester;

    const semesterOptions = [
        { label: t("semester_1"), value: "S1" },
        { label: t("semester_2"), value: "S2" },
    ]

    useEffect(() => {
        if (teacherId) {
            fetchClassesTeacher(teacherId);
        }
    }, [teacherId]);

    useEffect(() => {
        if (classes && classes?.length > 0) {
            const formatted = classes.map((cls: any) => ({
                id: cls.classId,
                name: cls.className
            }));
            setFormattedClasses(formatted);
        } else {
            setFormattedClasses([]);
        }
    }, [classes]);

    useEffect(() => {
        if (selectedClassId && classes?.length > 0) {
            const selectedClass = classes.find((c: any) => c.classId === selectedClassId);

            if (selectedClass && selectedClass.subjects) {
                const formattedSubjects: any[] = selectedClass.subjects.map((subject: any) => ({
                    id: subject.subjectId,
                    name: getSubjectLabelFromCode(subject.subjectCode) || subject.subjectName,
                    code: subject.subjectCode
                }));

                setSubjects(formattedSubjects);

                if (selectedSubjectId && !formattedSubjects.find(s => s.id === selectedSubjectId)) {
                    setSelectedSubjectId("");
                }
            } else {
                setSubjects([]);
            }
        } else {
            setSubjects([]);
        }
    }, [selectedClassId, classes, selectedSubjectId, t]);

    useEffect(() => {
        if (classId && subjectId && semester){
            setSelectedClassId(classId);
            setSelectedSubjectId(subjectId);
            setSelectedSemester(semester);
        } else {
            setSelectedClassId("");
            setSelectedSubjectId("");
            setSelectedSemester("");
        }
    }, [classId, subjectId, semester]);

    const handleClassChange = (value: string) => {
        setSelectedClassId(value);
        setSelectedSubjectId("");
        setSelectedSemester("");
    };

    const handleSubjectChange = (value: string) => {
        setSelectedSubjectId(value);
        setSelectedSemester("");
    };

    const handleSemesterChange = (value: string) => {
        setSelectedSemester(value);
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader
                    title={t("mark_matrix.title")}
                    breadcrumbItems={breadcrumbItems}
                />

                {!hasAllParams && (
                    <div className="mb-3 gap-2">
                        <div className="card">
                            <div className="card-body" style={{ zIndex: 999 }}>
                                <div className="row">
                                    <div className="col-md-4">
                                        <Form.Item label={t("class")}>
                                            <CommonSelectLite
                                                options={formattedClasses}
                                                optionLabelKey="name"
                                                optionValueKey="id"
                                                value={selectedClassId}
                                                onChange={handleClassChange}
                                            />
                                        </Form.Item>
                                    </div>

                                    <div className="col-md-4">
                                        <Form.Item label={t("subject")}>
                                            <CommonSelectLite
                                                options={subjects}
                                                optionLabelKey="name"
                                                optionValueKey="id"
                                                value={selectedSubjectId}
                                                onChange={handleSubjectChange}
                                                disabled={!selectedClassId || subjects?.length === 0}
                                            />
                                        </Form.Item>
                                    </div>

                                    <div className="col-md-4">
                                        <Form.Item label={t("semester")}>
                                            <CommonSelectLite
                                                options={semesterOptions}
                                                optionLabelKey="label"
                                                optionValueKey="value"
                                                value={selectedSemester}
                                                onChange={handleSemesterChange}
                                                disabled={!selectedClassId || subjects?.length === 0 || !selectedSubjectId}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {selectedClassId && selectedSubjectId && selectedSemester ? (
                    <TeacherStudentExamTypeMatrixTable
                        classId={selectedClassId}
                        subjectId={selectedSubjectId}
                        semester={selectedSemester}
                    />
                ) : (
                    <NoDataComponent />
                )}
            </div>
        </div>
    );
};

export default TeacherMarkStudents;